<script lang="ts">
  import { onMount } from 'svelte';
  import { invoke } from '@tauri-apps/api/core';
  import { Button } from '$lib/components/ui/button/index.js';
  import { Label } from '$lib/components/ui/label/index.js';
  import * as Select from '$lib/components/ui/select/index.js';
  import { RefreshCw } from '@lucide/svelte';
  import {
    sanFranciscanRTUSettings,
    primoRTUSettings,
    coffedRTUSettings,
    yoshanRTUSettings,
    selectedDeviceType
  } from '$lib/PersistedState.svelte';
  import { DeviceType } from '$lib/constants';
  import logger from '$lib/logger';
  import type { SerialPortInfo } from '$lib/types/Device';

  // Get current settings based on device type
  let portName = $state('');
  let slaveId = $state(1);

  // Initialize from persisted state based on device type
  $effect(() => {
    const deviceType = selectedDeviceType.value;
    let settings;

    if (deviceType === DeviceType.PRIMO) {
      settings = primoRTUSettings.current;
    } else if (deviceType === DeviceType.COFFED_SR30) {
      settings = coffedRTUSettings.current;
    } else if (deviceType === DeviceType.YOSHAN_RTU) {
      settings = yoshanRTUSettings.current;
    } else {
      // Default to San Franciscan RTU for backward compatibility
      settings = sanFranciscanRTUSettings.current;
    }

    portName = settings.portName || '';
    slaveId = settings.slaveId || 1;
  });

  // State
  let availablePorts = $state<string[]>([]);
  let isLoading = $state(false);
  let error = $state<string | null>(null);

  // Available slave IDs (common range for Modbus devices)
  const slaveIdOptions = Array.from({ length: 247 }, (_, i) => i + 1);

  /**
   * Load available serial ports
   */
  async function loadAvailablePorts() {
    try {
      isLoading = true;
      error = null;

      const ports = (await invoke('list_available_ports')) as SerialPortInfo[];
      logger.debug('Available ports:', ports);
      // Extract just the port names from the SerialPortInfo objects
      const portNames = ports.map((port) => port.port_name);
      availablePorts = portNames;

      // If no port is currently selected and ports are available, select the first one
      if (!portName && portNames.length > 0) {
        portName = portNames[0];
        const settings = getCurrentSettings();
        settings.portName = portName;
      }

      logger.debug('Loaded available serial ports:', portNames);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      error = `Failed to load serial ports: ${errorMessage}`;
      logger.error('Error loading serial ports:', errorMessage);
    } finally {
      isLoading = false;
    }
  }

  /**
   * Get the current settings based on device type
   */
  function getCurrentSettings() {
    const deviceType = selectedDeviceType.value;
    if (deviceType === DeviceType.PRIMO) {
      return primoRTUSettings.current;
    }
    if (deviceType === DeviceType.COFFED_SR30) {
      return coffedRTUSettings.current;
    }
    if (deviceType === DeviceType.YOSHAN_RTU) {
      return yoshanRTUSettings.current;
    }
    return sanFranciscanRTUSettings.current;
  }

  /**
   * Handle port selection change
   */
  function handlePortChange(value: string) {
    portName = value;

    // Update persisted state based on device type
    const settings = getCurrentSettings();
    settings.portName = portName;

    logger.info('Selected serial port:', portName);
  }

  /**
   * Handle slave ID change
   */
  function handleSlaveIdChange(value: string) {
    const id = parseInt(value);
    slaveId = id;

    // Update persisted state based on device type
    const settings = getCurrentSettings();
    settings.slaveId = id;

    logger.info('Selected slave ID:', id);
  }

  /**
   * Refresh available ports
   */
  async function refreshPorts() {
    await loadAvailablePorts();
  }

  // Load ports on component mount
  onMount(() => {
    loadAvailablePorts();
  });
</script>

<div class="grid grid-cols-2 gap-2">
  <div>
    <Label for="port-select">Serial Port</Label>
    <div class="flex gap-2">
      <Select.Root
        type="single"
        value={portName}
        onValueChange={handlePortChange}
        disabled={isLoading}
      >
        <Select.Trigger id="port-select" class="max-w-xs flex-1">
          {portName || 'Select serial port'}
        </Select.Trigger>
        <Select.Content>
          {#if availablePorts.length === 0}
            <Select.Item value="" disabled>No ports available</Select.Item>
          {:else}
            {#each availablePorts as port}
              <Select.Item value={port}>{port}</Select.Item>
            {/each}
          {/if}
        </Select.Content>
      </Select.Root>

      <Button
        variant="outline"
        size="icon"
        onclick={refreshPorts}
        disabled={isLoading}
        title="Refresh ports"
      >
        <RefreshCw class="h-4 w-4 {isLoading ? 'animate-spin' : ''}" />
      </Button>
    </div>

    {#if error}
      <p class="mt-1 text-sm text-red-600">{error}</p>
    {/if}

    {#if availablePorts.length === 0 && !isLoading && !error}
      <p class="mt-1 text-sm text-amber-600">
        No serial ports detected. Make sure your USB-to-RS485 adapter is connected.
      </p>
    {/if}
  </div>

  <div>
    <Label for="slave-select">Slave ID</Label>
    <Select.Root type="single" value={slaveId.toString()} onValueChange={handleSlaveIdChange}>
      <Select.Trigger id="slave-select" class="w-full max-w-xs">
        {slaveId}
      </Select.Trigger>
      <Select.Content>
        {#each slaveIdOptions as id}
          <Select.Item value={id.toString()}>{id}</Select.Item>
        {/each}
      </Select.Content>
    </Select.Root>
    <p class="text-muted-foreground mt-1 text-sm">
      The Modbus slave ID of your device (typically 1 for single devices)
    </p>
  </div>
</div>
